import axios, { InternalAxiosRequestConfig } from "axios";
import { LocalStorageKeys, localForageStorage } from "./storage.helper";
import { GuestAuthTokens } from "../types/public-booking.type";
import { shouldRefreshToken } from "./session.helper";
import { useGlobalStore } from "../stores/global.store";
import { extractErrorMessage } from "./error.helper";

// Public booking API client. Deliberately separate from `axiosApiInstance`
// (app/javascript/main/helpers/axios.helper.ts): that instance's 401 handler
// logs the *company owner* out and redirects to Routes.Main, which would be
// wrong for a guest browsing someone's booking page.
const onGuestRequestInterceptor = async (
  config: InternalAxiosRequestConfig<any>
) => {
  let tokens: GuestAuthTokens | null = await localForageStorage.getItem(
    LocalStorageKeys.GuestTokens
  );

  if (!tokens) return config;

  if (shouldRefreshToken(tokens.refreshedAt, tokens.expires_in * 1000)) {
    try {
      const { data } = await axios.post<GuestAuthTokens>(
        `${import.meta.env.VITE_RUBY_API_URL}/refresh`,
        null,
        { headers: { Authorization: `Bearer ${tokens.refresh_token}` } }
      );

      tokens = await localForageStorage.setItem(LocalStorageKeys.GuestTokens, {
        ...data,
        refreshedAt: new Date().getTime(),
      });
    } catch {
      await localForageStorage.removeItem(LocalStorageKeys.GuestTokens);
      return config;
    }
  }

  if (tokens) {
    config.headers.Authorization = `Bearer ${tokens.access_token}`;
  }

  return config;
};

export const guestApi = axios.create({
  baseURL: `${import.meta.env.VITE_RUBY_API_URL}/v1/public`,
});

guestApi.interceptors.request.use(onGuestRequestInterceptor);

guestApi.interceptors.response.use(null, async (error) => {
  if (axios.isAxiosError(error) && error.response?.status !== 401) {
    const globalStore = useGlobalStore();
    globalStore.triggerError(extractErrorMessage(error));
  }

  return Promise.reject(error);
});

export const setGuestTokens = async (data: {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}) => {
  await localForageStorage.setItem(LocalStorageKeys.GuestTokens, {
    ...data,
    refreshedAt: new Date().getTime(),
  });
};

export const clearGuestTokens = async () => {
  await localForageStorage.removeItem(LocalStorageKeys.GuestTokens);
};

export const isGuestLoggedIn = async (): Promise<boolean> => {
  const tokens = await localForageStorage.getItem<GuestAuthTokens>(
    LocalStorageKeys.GuestTokens
  );
  return !!tokens;
};
